import { useState, useCallback, useRef } from 'react';
import { Play, RotateCcw } from 'lucide-react';

const HERO_BG = "https://images.pexels.com/photos/17729221/pexels-photo-17729221.jpeg";
const COLS = 16;
const ROWS = 9;
const TOTAL = COLS * ROWS;

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Generate install order: column-by-column, bottom-to-top (like real billboard construction)
function generateInstallOrder() {
  const order = [];
  for (let col = 0; col < COLS; col++) {
    for (let row = ROWS - 1; row >= 0; row--) {
      order.push(row * COLS + col);
    }
  }
  // Add some realistic randomness: group into batches of 3-4 and shuffle within
  const batched = [];
  for (let i = 0; i < order.length; i += 3) {
    const batch = order.slice(i, i + 3);
    batched.push(...shuffleArray(batch));
  }
  return batched;
}

export default function LedDemo() {
  const [panels, setPanels] = useState(() => Array(TOTAL).fill(false));
  const [animating, setAnimating] = useState(false);
  const [mode, setMode] = useState('install'); // 'install' | 'powerOn'
  const [powered, setPowered] = useState(Array(TOTAL).fill(false));
  const [phase, setPhase] = useState('idle'); // 'idle' | 'installing' | 'installed' | 'powering' | 'done'
  const timeoutsRef = useRef([]);

  const clearAllTimeouts = () => {
    timeoutsRef.current.forEach(t => clearTimeout(t));
    timeoutsRef.current = [];
  };

  const reset = useCallback(() => {
    clearAllTimeouts();
    setPanels(Array(TOTAL).fill(false));
    setPowered(Array(TOTAL).fill(false));
    setAnimating(false);
    setPhase('idle');
    setMode('install');
  }, []);

  const runInstall = useCallback(() => {
    clearAllTimeouts();
    setAnimating(true);
    setPhase('installing');
    setPanels(Array(TOTAL).fill(false));
    setPowered(Array(TOTAL).fill(false));

    const order = generateInstallOrder();
    const baseDelay = 40;

    order.forEach((idx, i) => {
      const t = setTimeout(() => {
        setPanels(prev => {
          const next = [...prev];
          next[idx] = true;
          return next;
        });
        if (i === order.length - 1) {
          const t2 = setTimeout(() => {
            setPhase('installed');
            setAnimating(false);
          }, 300);
          timeoutsRef.current.push(t2);
        }
      }, i * baseDelay);
      timeoutsRef.current.push(t);
    });
  }, []);

  const runPowerOn = useCallback(() => {
    clearAllTimeouts();
    setAnimating(true);
    setPhase('powering');

    // Power on in a sweep pattern: left to right with some scatter
    const order = [];
    for (let col = 0; col < COLS; col++) {
      const colPanels = [];
      for (let row = 0; row < ROWS; row++) {
        colPanels.push(row * COLS + col);
      }
      order.push(...shuffleArray(colPanels));
    }

    const baseDelay = 25;

    order.forEach((idx, i) => {
      const t = setTimeout(() => {
        setPowered(prev => {
          const next = [...prev];
          next[idx] = true;
          return next;
        });
        if (i === order.length - 1) {
          const t2 = setTimeout(() => {
            setPhase('done');
            setAnimating(false);
          }, 400);
          timeoutsRef.current.push(t2);
        }
      }, i * baseDelay);
      timeoutsRef.current.push(t);
    });
  }, []);

  const runFullSequence = useCallback(() => {
    clearAllTimeouts();
    setAnimating(true);
    setPhase('installing');
    setPanels(Array(TOTAL).fill(false));
    setPowered(Array(TOTAL).fill(false));

    const order = generateInstallOrder();
    const installDelay = 35;
    const installDuration = order.length * installDelay + 600;

    // Phase 1: Install panels
    order.forEach((idx, i) => {
      const t = setTimeout(() => {
        setPanels(prev => {
          const next = [...prev];
          next[idx] = true;
          return next;
        });
      }, i * installDelay);
      timeoutsRef.current.push(t);
    });

    // Phase 2: Power on
    const t = setTimeout(() => {
      setPhase('powering');
      const powerOrder = [];
      for (let col = 0; col < COLS; col++) {
        const colPanels = [];
        for (let row = 0; row < ROWS; row++) {
          colPanels.push(row * COLS + col);
        }
        powerOrder.push(...shuffleArray(colPanels));
      }

      powerOrder.forEach((idx, i) => {
        const t2 = setTimeout(() => {
          setPowered(prev => {
            const next = [...prev];
            next[idx] = true;
            return next;
          });
          if (i === powerOrder.length - 1) {
            const t3 = setTimeout(() => {
              setPhase('done');
              setAnimating(false);
            }, 400);
            timeoutsRef.current.push(t3);
          }
        }, i * 25);
        timeoutsRef.current.push(t2);
      });
    }, installDuration);
    timeoutsRef.current.push(t);
  }, []);

  return (
    <main className="bg-[#1a1a1a] min-h-screen">
      {/* Controls */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="mb-6">
          <p className="text-[#7AC943] text-sm uppercase tracking-[0.2em] font-semibold mb-2">LED Billboard Demo</p>
          <h1 className="font-heading text-3xl md:text-4xl text-white tracking-tight">
            Panel-by-Panel Construction
          </h1>
          <p className="text-gray-400 text-sm mt-2 max-w-lg">
            Watch how a large LED billboard is assembled from individual screen panels, then powered on to display the final image.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          <button
            onClick={runFullSequence}
            disabled={animating}
            data-testid="demo-full-sequence"
            className="inline-flex items-center gap-2 bg-[#7AC943] text-black text-sm font-semibold uppercase tracking-[0.1em] px-6 py-3 rounded-sm hover:bg-[#68B038] transition-colors duration-300 disabled:opacity-40"
          >
            <Play size={16} />
            Full Build
          </button>
          <button
            onClick={runInstall}
            disabled={animating}
            data-testid="demo-install-btn"
            className="inline-flex items-center gap-2 border border-white/30 text-white text-sm font-medium px-6 py-3 rounded-sm hover:bg-white/10 transition-colors duration-300 disabled:opacity-40"
          >
            Install Panels
          </button>
          <button
            onClick={runPowerOn}
            disabled={animating || phase === 'idle'}
            data-testid="demo-power-btn"
            className="inline-flex items-center gap-2 border border-white/30 text-white text-sm font-medium px-6 py-3 rounded-sm hover:bg-white/10 transition-colors duration-300 disabled:opacity-40"
          >
            Power On
          </button>
          <button
            onClick={reset}
            data-testid="demo-reset-btn"
            className="inline-flex items-center gap-2 border border-white/30 text-gray-400 text-sm font-medium px-6 py-3 rounded-sm hover:bg-white/10 hover:text-white transition-colors duration-300"
          >
            <RotateCcw size={14} />
            Reset
          </button>
        </div>

        {/* Phase indicator */}
        <div className="mb-4 flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${
            phase === 'idle' ? 'bg-gray-600' :
            phase === 'installing' ? 'bg-yellow-400 animate-pulse' :
            phase === 'installed' ? 'bg-yellow-400' :
            phase === 'powering' ? 'bg-blue-400 animate-pulse' :
            'bg-[#7AC943]'
          }`} />
          <span className="text-gray-400 text-xs uppercase tracking-wider">
            {phase === 'idle' && 'Ready'}
            {phase === 'installing' && 'Installing panels...'}
            {phase === 'installed' && 'Panels installed - ready to power on'}
            {phase === 'powering' && 'Powering on...'}
            {phase === 'done' && 'Billboard live'}
          </span>
        </div>

        {/* Billboard */}
        <div
          data-testid="led-billboard"
          className="relative w-full overflow-hidden rounded-lg border-4 border-gray-800 shadow-2xl"
          style={{ aspectRatio: `${COLS}/${ROWS}` }}
        >
          {/* Base image (hidden until powered) */}
          <img
            src={HERO_BG}
            alt="City skyline"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Dark base (no panels) */}
          <div className="absolute inset-0 bg-[#0a0a0a]" />

          {/* Panel grid */}
          <div
            className="absolute inset-0 grid"
            style={{
              gridTemplateColumns: `repeat(${COLS}, 1fr)`,
              gridTemplateRows: `repeat(${ROWS}, 1fr)`,
              gap: '1px',
              background: '#111',
            }}
          >
            {Array.from({ length: TOTAL }, (_, i) => {
              const row = Math.floor(i / COLS);
              const col = i % COLS;
              const isInstalled = panels[i];
              const isPowered = powered[i];

              return (
                <div
                  key={i}
                  className="relative overflow-hidden"
                  style={{
                    transition: 'all 0.3s ease-out',
                  }}
                >
                  {/* Empty slot */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: isInstalled ? '#1a1a1a' : '#0a0a0a',
                      transition: 'background 0.2s ease',
                    }}
                  />

                  {/* Installed panel (dark screen) */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: isInstalled ? '#222' : 'transparent',
                      opacity: isInstalled ? 1 : 0,
                      transition: 'all 0.3s ease-out',
                      transform: isInstalled ? 'scale(1)' : 'scale(0.7)',
                      boxShadow: isInstalled ? 'inset 0 0 2px rgba(255,255,255,0.05)' : 'none',
                    }}
                  >
                    {/* Panel border detail */}
                    <div className="absolute inset-[1px] border border-gray-700/30 rounded-[1px]" />
                  </div>

                  {/* Powered on - shows image slice */}
                  <div
                    className="absolute inset-0 overflow-hidden"
                    style={{
                      opacity: isPowered ? 1 : 0,
                      transition: 'opacity 0.15s ease-out',
                    }}
                  >
                    <div
                      className="absolute"
                      style={{
                        width: `${COLS * 100}%`,
                        height: `${ROWS * 100}%`,
                        left: `${-col * 100}%`,
                        top: `${-row * 100}%`,
                        backgroundImage: `url(${HERO_BG})`,
                        backgroundSize: '100% 100%',
                      }}
                    />
                    {/* Scan line / power-on flash */}
                    <div
                      className="absolute inset-0"
                      style={{
                        background: isPowered ? 'transparent' : 'rgba(122, 201, 67, 0.3)',
                        transition: 'background 0.5s ease-out 0.1s',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Subtle frame reflection */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%)',
          }} />
        </div>

        {/* Billboard frame details */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-4">
            <span className="text-gray-600 text-xs font-mono">{COLS}x{ROWS} PANEL ARRAY</span>
            <span className="text-gray-700 text-xs">|</span>
            <span className="text-gray-600 text-xs font-mono">{TOTAL} MODULES</span>
          </div>
          <span className="text-gray-600 text-xs font-mono">EASTWIDE ELECTRICAL</span>
        </div>
      </div>
    </main>
  );
}
